import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { Trash2, Mail, Phone, Calendar } from 'lucide-react';
import axios from 'axios';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useCurrentAdmin } from '../../lib/useCurrentAdmin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const { isAdmin } = useCurrentAdmin();

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact deleted successfully!');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    } finally {
      setPendingDeleteId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <p className="text-gray-600">Loading contacts...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Contact Form Submissions</h1>
            <p className="text-gray-600 mt-2">{contacts.length} total submissions</p>
          </div>
        </div>

        {contacts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No contact form submissions yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contacts.map((contact) => (
              <Card key={contact.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold mb-1">{contact.name}</h3>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <span className="flex items-center">
                              <Mail className="h-4 w-4 mr-1" style={{ color: '#00CED1' }} />
                              <a href={`mailto:${contact.email}`} className="hover:underline">
                                {contact.email}
                              </a>
                            </span>
                            {contact.phone && (
                              <span className="flex items-center">
                                <Phone className="h-4 w-4 mr-1" style={{ color: '#00CED1' }} />
                                {contact.phone}
                              </span>
                            )}
                            <span className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" style={{ color: '#00CED1' }} />
                              {formatDate(contact.createdAt)}
                            </span>
                          </div>
                        </div>
                        {isAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPendingDeleteId(contact.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`admin-contacts-delete-${contact.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="mb-3">
                        <span 
                          className="font-semibold text-sm"
                          style={{ color: '#00CED1' }}
                        >
                          Subject:
                        </span>
                        <span className="ml-2 text-gray-900">{contact.subject}</span>
                      </div>
                      
                      <div 
                        className="p-4 rounded-lg border-l-4 bg-gray-50"
                        style={{ borderColor: '#00CED1' }}
                      >
                        <p className="text-gray-700 whitespace-pre-wrap">{contact.message}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <ConfirmDialog
          open={!!pendingDeleteId}
          title="Delete this contact submission?"
          description="The submission will be permanently removed. This action cannot be undone."
          confirmLabel="Delete"
          destructive
          onCancel={() => setPendingDeleteId(null)}
          onConfirm={() => handleDelete(pendingDeleteId)}
          testId="admin-contacts-confirm-delete"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminContacts;
